import cn from "classnames";
import format from "date-fns/format";
import isValid from "date-fns/isValid";
import { enGB, ru, et, lt, lv, pl, fi } from "date-fns/locale";
import { useRouter } from "next/router";
import { useRef, useState } from "react";
import { DayPicker, useNavigation, useDayRender } from "react-day-picker";

import i from "@styles/icons.module.scss";

import s from "./DatePicker.module.scss";

const locales = { en: enGB, ru, et, lt, lv, pl, fi };

const YearView = ({ currentYear, onYearSelect, locale }) => {
  const startYear = Math.floor(currentYear / 12) * 12;
  const years = Array.from({ length: 12 }, (_, i) => startYear + i);

  const handlePrevious = () => {
    onYearSelect(startYear - 12, false);
  };

  const handleNext = () => {
    onYearSelect(startYear + 12, false);
  };

  return (
    <div className={cn(s.monthView, s.fadeIn)}>
      <div className={s.caption}>
        <icon
          onClick={handlePrevious}
          className={cn(i.arrowLeft, s.arrow, s.left)}
        />
        <span>
          {startYear} - {startYear + 11}
        </span>
        <icon
          onClick={handleNext}
          className={cn(i.arrowRight, s.arrow, s.right)}
        />
      </div>
      <div className={s.monthGrid}>
        {years.map((year) => (
          <button
            key={year}
            type="button"
            className={cn(s.monthItem, year === currentYear && s.selected)}
            onClick={() => onYearSelect(year, true)}
          >
            {year}
          </button>
        ))}
      </div>
    </div>
  );
};

const MonthView = ({ currentMonth, currentYear, onMonthSelect, locale }) => {
  const months = Array.from({ length: 12 }, (_, i) => {
    const date = new Date(currentYear, i, 1);
    return {
      index: i,
      name: format(date, "LLLL", { locale: locales[locale] || enGB }),
    };
  });

  const handlePrevious = () => {
    onMonthSelect(new Date(currentYear - 1, currentMonth.getMonth(), 1), false);
  };

  const handleNext = () => {
    onMonthSelect(new Date(currentYear + 1, currentMonth.getMonth(), 1), false);
  };

  return (
    <div className={cn(s.monthView, s.fadeIn)}>
      <div className={s.caption}>
        <icon
          onClick={handlePrevious}
          className={cn(i.arrowLeft, s.arrow, s.left)}
        />
        <span>{currentYear}</span>
        <icon
          onClick={handleNext}
          className={cn(i.arrowRight, s.arrow, s.right)}
        />
      </div>
      <div className={s.monthGrid}>
        {months.map((month) => (
          <button
            key={month.index}
            type="button"
            className={cn(
              s.monthItem,
              month.index === currentMonth.getMonth() && s.selected,
            )}
            onClick={() =>
              onMonthSelect(new Date(currentYear, month.index, 1), true)
            }
          >
            {month.name.slice(0, 3)}
          </button>
        ))}
      </div>
    </div>
  );
};

const CustomCaption = ({ displayMonth, onViewChange }) => {
  const { goToMonth, nextMonth, previousMonth } = useNavigation();
  const { locale } = useRouter();

  return (
    <div className={s.caption}>
      <icon
        onClick={() => previousMonth && goToMonth(previousMonth)}
        className={cn(
          i.arrowLeft,
          s.arrow,
          s.left,
          !previousMonth && s.disabled,
        )}
      />
      <button className={s.captionButton} onClick={onViewChange} type="button">
        {format(displayMonth, "LLLL yyyy", { locale: locales[locale] || enGB })}
      </button>
      <icon
        onClick={() => nextMonth && goToMonth(nextMonth)}
        className={cn(i.arrowRight, s.arrow, s.right, !nextMonth && s.disabled)}
      />
    </div>
  );
};

const CustomDay = (props) => {
  const buttonRef = useRef(null);
  const dayRender = useDayRender(props.date, props.displayMonth, buttonRef);

  const isSelected = dayRender.activeModifiers.selected;
  const isDisabled = dayRender.activeModifiers.disabled;
  const isOutside = dayRender.activeModifiers.outside;

  const handleClick = (e) => {
    !isDisabled && dayRender.buttonProps?.onClick?.(e);
  };

  if (!dayRender.isButton) {
    return <div {...dayRender.divProps} />;
  }

  return (
    <button
      {...dayRender.buttonProps}
      type="button"
      ref={buttonRef}
      onClick={handleClick}
      className={cn(
        s.day,
        isSelected && s.selected,
        isOutside && s.outside,
        isDisabled && s.disabled,
      )}
    >
      {props.date.getDate()}
    </button>
  );
};

const DatePicker = ({
  className,
  onSelect,
  closePopper,
  mode = "single",
  defaultMonth,
  ...props
}) => {
  const { locale } = useRouter();
  const [viewMode, setViewMode] = useState("days"); // 'days', 'months', 'years'
  const [currentMonth, setCurrentMonth] = useState(
    isValid(new Date(defaultMonth)) ? new Date(defaultMonth) : new Date(),
  );

  const handleOnSelect = (...args) => {
    const selectedDate = args[0];
    if (selectedDate && isValid(selectedDate)) {
      onSelect(...args);
    }

    if (closePopper) closePopper();
  };

  const handleViewChange = () => {
    if (viewMode === "days") {
      setViewMode("years");
    } else if (viewMode === "years") {
      setViewMode("months");
    }
  };

  const handleYearSelect = (year, shouldChangeView) => {
    if (typeof year === "number") {
      const newDate = new Date(currentMonth);
      newDate.setFullYear(year);
      setCurrentMonth(newDate);

      if (shouldChangeView) {
        setViewMode("months");
      }
    }
  };

  const handleMonthSelect = (date, shouldChangeView) => {
    setCurrentMonth(date);

    if (shouldChangeView) {
      setViewMode("days");
    }
  };

  const modifiersClassNames = {
    selected: s.selected,
    today: s.today,
    disabled: s.disabled,
    outside: s.outside,
  };

  return (
    <div className={cn(s.datePicker, className)}>
      {/* <div className={s.triangleWrap}>
        <div className={s.triangle} />
      </div> */}

      {viewMode === "years" && (
        <YearView
          currentYear={currentMonth.getFullYear()}
          onYearSelect={handleYearSelect}
          locale={locale}
        />
      )}

      {viewMode === "months" && (
        <MonthView
          currentMonth={currentMonth}
          currentYear={currentMonth.getFullYear()}
          onMonthSelect={handleMonthSelect}
          locale={locale}
        />
      )}

      {viewMode === "days" && (
        <DayPicker
          {...props}
          month={currentMonth}
          onMonthChange={setCurrentMonth}
          defaultMonth={currentMonth}
          mode={mode}
          onSelect={handleOnSelect}
          ISOWeek
          fixedWeeks
          className={s.commonDayStyle}
          modifiersClassNames={modifiersClassNames}
          locale={locales[locale]}
          components={{
            Day: CustomDay,
            Caption: (props) => (
              <CustomCaption {...props} onViewChange={handleViewChange} />
            ),
          }}
        />
      )}
    </div>
  );
};

export default DatePicker;
